import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { FaArrowLeft, FaTrophy, FaMedal, FaCalculator, FaCheck, FaTimes, FaInfoCircle, FaFlag } from '../../components/Icons';
import SancionModal from '../../components/SancionModal';
import { Carrera, ResultadoCarrera, Socio } from '../../models/types';
import { sortRacesByName } from '../../utils/sortRacesByName';
import { SectionHeaderComp } from '../ligas/LastresLigaPage';

// Función para formatear fechas
const formatearFecha = (fechaStr: string) => {
  if (!fechaStr) return 'Fecha no disponible';
  
  try {
    const fecha = new Date(fechaStr);
    return fecha.toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  } catch (error) {
    console.error('Error al formatear fecha:', error);
    return fechaStr;
  }
};

const Container = styled.div`
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 8px;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  color: #1a1a2e;
`;

const BackButton = styled.button`
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  cursor: pointer;
  display: flex;
  align-items: center;
  
  &:hover {
    background-color: #5a6268;
  }
  
  svg {
    margin-right: 8px;
  }
`;

const ActionButton = styled.button`
  background-color: #e94560;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  margin-left: 10px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #d03d55;
    transform: translateY(-2px);
  }
  
  svg {
    margin-right: 8px;
  }
`;

const Card = styled.div`
  background-color: white;
  border-radius: 8px;
  padding: 15px; // Adjusted default padding
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
  overflow-x: auto; // Keep for tables that might be inside a Card

  @media (max-width: 768px) {
    padding: 10px; // Reduced padding for mobile
  }
`;  

const CardTitle = styled.h3`
  color: #0f3460;
  margin-top: 0;
  margin-bottom: 15px;
  font-size: 1.2rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 800px;
`;

const Th = styled.th`
  background-color: #0f3460;
  color: white;
  text-align: left;
  padding: 12px 15px;
  position: sticky;
  top: 0;
  z-index: 10;
`;

const ThCarrera = styled(Th)`
  min-width: 120px;
  text-align: center;
  white-space: pre-line;
  line-height: 1.2;
  
  /* Add a smaller font size for the circuit name */
  & span.circuit-name {
    display: block;
    font-size: 0.85em;
    margin-top: 4px;
    font-weight: normal;
    opacity: 0.9;
  }
`;

const ThPuntos = styled(Th)`
  min-width: 120px;
  text-align: center;
  background-color: #e94560;
`;

const Td = styled.td`
  padding: 12px 15px;
  border-bottom: 1px solid #ddd;
`;

const TdPuntos = styled(Td)`
  text-align: center;
  font-weight: bold;
`;

const TdCarrera = styled(Td)`
  text-align: center;
`;

const TdPiloto = styled(Td)`
  font-weight: bold;
`;

const TdTotal = styled(Td)`
  text-align: center;
  font-weight: bold;
  background-color: #f8f9fa;
`;

const TdTotalDescarte = styled(Td)`
  text-align: center;
  font-weight: bold;
  background-color: #e2f0ff;
  color: #0f3460;
`;

const PosicionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Posicion = styled.div<{ pos: number }>`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 10px;
  
  ${props => {
    if (props.pos === 1) {
      return `
        background-color: gold;
        color: #333;
      `;
    } else if (props.pos === 2) {
      return `
        background-color: silver;
        color: #333;
      `;
    } else if (props.pos === 3) {
      return `
        background-color: #cd7f32;
        color: white;
      `;
    } else {
      return `
        background-color: #f0f0f0;
        color: #333;
      `;
    }
  }}
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
`;

const InfoMessage = styled.div`
  background-color: #e2f0ff;
  border-left: 4px solid #0f3460;
  padding: 15px;
  margin-bottom: 20px;
  border-radius: 4px;
`;

const DesktopOnly = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileCardContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

// Extend Card for specific mobile pilot card styling
const PilotoCardMobile = styled(Card)`
  h4 {
    margin-top: 0;
    margin-bottom: 10px;
    color: ${({ theme }) => theme.colors?.primary?.main || '#0f3460'};
  }
  p {
    margin: 5px 0;
    font-size: 0.9rem;
  }
  .position-badge {
    font-weight: bold;
    color: ${({ theme }) => theme.colors?.secondary?.main || '#e94560'};
  }
  .points {
    font-weight: bold;
  }
`;

// Interfaz para los resultados de un piloto en todas las carreras
interface ResultadoPiloto {
  socioId: string;
  nombre?: string;
  apellido?: string;
  display_name?: string;
  resultadosPorCarrera: Map<string, ResultadoCarrera | null>;
  puntosTotales: number;
  puntosTotalesConDescarte: number;
  peorResultado: {
    carreraId: string;
    puntos: number;
  } | null;
}

const ClasificacionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, theme } = useContext(AppContext);
  
  // Estados
  const [liga, setLiga] = useState<any>(null);
  const [carreras, setCarreras] = useState<Carrera[]>([]);
  const [resultadosPilotos, setResultadosPilotos] = useState<ResultadoPiloto[]>([]);
  const [expandedSections, setExpandedSections] = useState<{
    general: boolean;
    carreras: { [key: string]: boolean };
  }>({
    general: true,
    carreras: {}
  });
  const [sancionSeleccionada, setSancionSeleccionada] = useState<any>(null);
  
  // Inicializar estados de expansión
  useEffect(() => {
    if (data?.carreras) {
      // Ordenar carreras por fecha para encontrar la más reciente
      const carrerasOrdenadas = [...data.carreras].sort((a, b) => 
        new Date(b.fecha).getTime() - new Date(a.fecha).getTime()
      );
      
      // Encontrar la última carrera con fecha pasada
      const now = new Date();
      const ultimaCarrera = carrerasOrdenadas.find(carrera => new Date(carrera.fecha) <= now);
      
      // Inicializar estado de expansión de carreras
      const carrerasState = data.carreras.reduce((acc, carrera) => {
        acc[carrera.id] = false; // Por defecto, todas colapsadas
        return acc;
      }, {} as Record<string, boolean>);
      
      // Expandir automáticamente la última carrera si existe
      if (ultimaCarrera) {
        carrerasState[ultimaCarrera.id] = true;
      }
      
      setExpandedSections({
        general: false, // Clasificación general colapsada por defecto
        carreras: carrerasState
      });
    }
  }, [data?.carreras]);
  
  // Función para alternar el estado de una sección
  const toggleSection = (section: 'general' | string) => {
    if (section === 'general') {
      setExpandedSections(prev => ({
        ...prev,
        general: !prev.general
      }));
    } else {
      setExpandedSections(prev => ({
        ...prev,
        carreras: {
          ...prev.carreras,
          [section]: !prev.carreras[section]
        }
      }));
    }
  };
  
  useEffect(() => {
    try {
      // Buscar la liga
      const ligaEncontrada = data.ligas.find(l => l.id === id);
      if (!ligaEncontrada) return;
      
      setLiga(ligaEncontrada);
      
      // Obtener todas las carreras de la liga (solo las publicadas)
      const carrerasLiga = data.carreras.filter(c => c.ligaId === id && c.publicada !== false);
console.log(`DEBUG - ClasificacionDetail - Encontradas ${carrerasLiga.length} carreras publicadas para la liga ${id}`);

// Ordenar carreras por nombre
const carrerasOrdenadas = sortRacesByName([...carrerasLiga]);
setCarreras(carrerasOrdenadas);
      
      // Obtener todos los pilotos de la liga usando el campo categoria de la liga
      // Si la liga no tiene categoria, intentar detectarla del nombre (compatibilidad con ligas antiguas)
      let categoriaLiga = ligaEncontrada.categoria || '';
      
      // Si no hay categoria definida, intentar detectarla del nombre (para ligas antiguas)
      if (!categoriaLiga) {
        if (ligaEncontrada.nombre === 'PRO' || ligaEncontrada.nombre.includes('PRO')) {
          categoriaLiga = 'PRO';
        } else if (ligaEncontrada.nombre === 'ADV' || ligaEncontrada.nombre.includes('ADV')) {
          categoriaLiga = 'ADV';
        } else if (ligaEncontrada.nombre === 'NOV' || ligaEncontrada.nombre.includes('NOV')) {
          categoriaLiga = 'NOV';
        }
      }
      
      console.log(`DEBUG - ClasificacionDetail - Usando categoría: ${categoriaLiga} para la liga ${ligaEncontrada.nombre}`);
      
      const pilotosLiga = data.socios.filter(socio => {
        const categoriaUpper = (socio.Categoria || '').trim().toUpperCase();
        const categoriaLigaUpper = categoriaLiga.trim().toUpperCase();
        return categoriaUpper === categoriaLigaUpper;
      });
      
      console.log(`DEBUG - ClasificacionDetail - Encontrados ${pilotosLiga.length} pilotos para la liga ${ligaEncontrada.nombre} (categoría: ${categoriaLiga})`);
      
      // Calcular resultados para cada piloto
      const resultados: ResultadoPiloto[] = pilotosLiga.map(piloto => {
        // Crear mapa de resultados por carrera
        const resultadosPorCarrera = new Map<string, ResultadoCarrera | null>();
        
        // Inicializar con null para todas las carreras
        carrerasOrdenadas.forEach(carrera => {
          resultadosPorCarrera.set(carrera.id, null);
        });
        
        // Rellenar con resultados reales donde existan
        carrerasOrdenadas.forEach(carrera => {
          const resultado = carrera.resultados.find(r => r.socioId === piloto.id);
          if (resultado) {
            resultadosPorCarrera.set(carrera.id, resultado);
          }
        });
        
        // Calcular puntos totales
        let puntosTotales = 0;
        const resultadosConPuntos: { carreraId: string, puntos: number }[] = [];
        
        resultadosPorCarrera.forEach((resultado, carreraId) => {
          if (resultado) {
            // Recalcular los puntos totales para asegurarnos de que las sanciones están aplicadas correctamente
            const carrera = carrerasOrdenadas.find(c => c.id === carreraId);
            if (!carrera) {
              puntosTotales += resultado.puntosTotales || 0;
              resultadosConPuntos.push({
                carreraId,
                puntos: resultado.puntosTotales || 0
              });
              return;
            }
            
            // Puntos base por posición
            let puntos = resultado.puntos;
            
            // Puntos adicionales por extras
            let puntosExtras = resultado.puntosAdicionales || 0; // Usar 0 si es undefined
            
            // Puntos de sanciones
            let puntosSanciones = 0;
            
            // Buscar sanciones aplicadas a este piloto en esta carrera
            const sancionesPiloto = data.sanciones.filter(sancion => 
              sancion.socioId === piloto.id && sancion.carreraId === carreraId
            );
            
            // Sumar los puntos de las sanciones encontradas
            console.log(`DEBUG - Sanciones para piloto ${piloto.id} en carrera ${carreraId}:`, sancionesPiloto);
            sancionesPiloto.forEach(sancion => {
              console.log(`DEBUG - Sanción: ${sancion.nombre}, Puntos: ${sancion.puntosPenalizacion}`);
              puntosSanciones += sancion.puntosPenalizacion;
            });
            console.log(`DEBUG - Total puntos sanciones para piloto ${piloto.id}: ${puntosSanciones}`);
            
            // Calcular total
            // Si es DNF, siempre 1 punto de consolación (a menos que tenga sanciones negativas)
            // Si no se ha presentado, siempre 0 puntos
            let puntosRecalculados = 0;
            if (resultado.noParticipacion) {
              // No se presentó, siempre 0 puntos
              puntosRecalculados = 0;
            } else if (resultado.dnf) {
              // DNF: 1 punto de consolación + sanciones (si son positivas)
              puntosRecalculados = 1 + (puntosSanciones > 0 ? puntosSanciones : 0);
            } else {
              // Normal: puntos por posición + adicionales + sanciones
              puntosRecalculados = puntos + puntosExtras + puntosSanciones;
            }
            
            puntosTotales += puntosRecalculados;
            resultadosConPuntos.push({
              carreraId,
              puntos: puntosRecalculados
            });
          } else {
            // Para carreras sin resultados (futuras), añadir un resultado con 0 puntos
            // Esto es importante para el cálculo correcto del descarte
            resultadosConPuntos.push({
              carreraId,
              puntos: 0
            });
          }
        });
        
        // Encontrar el peor resultado (si hay al menos una carrera)
        let peorResultado: { carreraId: string, puntos: number } | null = null;
        
        if (resultadosConPuntos.length > 0) {
          // Ordenar los resultados de menor a mayor puntuación
          const resultadosOrdenados = [...resultadosConPuntos].sort((a, b) => a.puntos - b.puntos);
          
          // El peor resultado es el primero (menor puntuación)
          peorResultado = resultadosOrdenados[0];
          
          console.log(`DEBUG - Peor resultado para ${piloto.display_name || piloto.nombre}: ${peorResultado.puntos} puntos en carrera ${peorResultado.carreraId}`);
        }
        
        // Calcular puntos totales con descarte
        let puntosTotalesConDescarte = puntosTotales;
        
        // Solo aplicar el descarte si hay más de una carrera y el peor resultado existe
        if (peorResultado && carrerasOrdenadas.length > 1) {
          // Si el peor resultado tiene puntos (no es una carrera futura con 0 puntos)
          // o si todas las carreras tienen resultados, aplicar el descarte
          if (peorResultado.puntos > 0 || resultadosConPuntos.length === carrerasOrdenadas.length) {
            puntosTotalesConDescarte = puntosTotales - peorResultado.puntos;
            console.log(`DEBUG - Descartando ${peorResultado.puntos} puntos para ${piloto.display_name || piloto.nombre}, nuevo total: ${puntosTotalesConDescarte}`);
          } else {
            // Si el peor resultado es 0 puntos y hay carreras sin resultados,
            // no aplicar el descarte (mantener el total original)
            console.log(`DEBUG - No se descarta para ${piloto.display_name || piloto.nombre} porque el peor resultado es 0 en una carrera futura`);
          }
        }
        
        return {
          socioId: piloto.id,
          nombre: piloto.nombre,
          apellido: piloto.apellido,
          display_name: piloto.display_name,
          resultadosPorCarrera,
          puntosTotales,
          puntosTotalesConDescarte,
          peorResultado
        };
      });
      
      // Ordenar por puntos totales con descarte (de mayor a menor)
      const resultadosOrdenados = [...resultados].sort((a, b) => 
        b.puntosTotalesConDescarte - a.puntosTotalesConDescarte
      );
      
      setResultadosPilotos(resultadosOrdenados);
  } catch (error) {
      console.error('Error al procesar resultados:', error);
  }
  }, [id, data]);
  
  // Función para formatear la fecha
  const formatearFecha = (fecha: string) => {
    if (!fecha) return '';
    const options: Intl.DateTimeFormatOptions = { 
      day: '2-digit', 
      month: '2-digit',
      year: 'numeric'
    };
    try {
      return new Date(fecha).toLocaleDateString('es-ES', options);
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return fecha;
    }
  };
  

  
  // Función para renderizar el nombre de la carrera formateado
  const renderizarNombreCarrera = (carrera: Carrera) => {
    return (
      <>
        <div>{carrera.nombre} ({formatearFecha(carrera.fecha)})</div>
        {carrera.circuito && <span className="circuit-name">{carrera.circuito}</span>}
      </>
    );
  };
          
  // Función para renderizar el valor de puntos con color según si es el peor resultado
  const renderizarPuntos = (piloto: ResultadoPiloto, carreraId: string) => {
    const resultado = piloto.resultadosPorCarrera.get(carreraId);
    
    if (!resultado) {
      return '-';
    }
    
    // Recalcular los puntos totales para asegurarnos de que las sanciones están aplicadas correctamente
    const carrera = carreras.find(c => c.id === carreraId);
    if (!carrera || !resultado) return resultado.puntos || '-';
    
    // Puntos base por posición
    const puntos = resultado.puntos || 0;
    
    // Puntos adicionales por extras
    const puntosExtras = resultado.puntosAdicionales || 0;
    
    // Puntos de sanciones
    let puntosSanciones = 0;
    
    // Buscar sanciones aplicadas a este piloto en esta carrera
    const sancionesPiloto = data.sanciones?.filter(sancion => 
      sancion.socioId === piloto.socioId && sancion.carreraId === carreraId
    ) || [];
    
    // Sumar los puntos de las sanciones encontradas
    sancionesPiloto.forEach(sancion => {
      puntosSanciones += sancion.puntosPenalizacion || 0;
    });
    
    // Calcular total (si es DNF, siempre 1 punto de consolación)
    const total = resultado.dnf ? 
      (1 + (puntosSanciones > 0 ? puntosSanciones : 0)) : 
      (puntos + puntosExtras + puntosSanciones);
    
    const esPeorResultado = piloto.peorResultado && 
                         piloto.peorResultado.carreraId === carreraId && 
                         carreras.length > 1;
    
    return (
      <span style={{ 
        color: esPeorResultado ? '#dc3545' : 'inherit',
        textDecoration: esPeorResultado ? 'line-through' : 'none',
        opacity: esPeorResultado ? 0.7 : 1
      }}>
        {total}
      </span>
    );
  };
  
  if (!liga) {
    return (
      <Container>
        <Header>
          <Title>Liga no encontrada</Title>
          <BackButton onClick={() => navigate('/clasificaciones')}>
            <FaArrowLeft /> Volver
          </BackButton>
        </Header>
        <Card>
          <EmptyMessage>La liga especificada no existe o está cargando...</EmptyMessage>
        </Card>
      </Container>
    );
  }

  return (
    <Container>
      <SectionHeaderComp icon={<FaFlag style={{ color: '#e4312e' }} />}>
        Detalle de carreras: {liga.nombre}
      </SectionHeaderComp>
      
      {carreras.length === 0 ? (
        <Card>
          <EmptyMessage>No hay carreras registradas para esta liga.</EmptyMessage>
        </Card>
      ) : (
        <div>
          {/* Podio estilo F1 para los tres primeros (Desktop) */}
          <DesktopOnly>
            {resultadosPilotos.length >= 3 && (
            <div style={{ marginBottom: '30px' }}>
              <h2 style={{ textAlign: 'center', marginBottom: '20px', color: '#1a1a2e' }}>Podio</h2>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'flex-end',
                gap: '20px',
                padding: '20px 0',
                background: 'linear-gradient(180deg, rgba(15,52,96,0.1) 0%, rgba(233,69,96,0.05) 100%)',
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
              }}>
                {/* Segundo lugar */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  marginBottom: '15px'
                }}>
                  <div style={{ 
                    width: '100px', 
                    height: '100px',
                    position: 'relative',
                    marginBottom: '10px'
                  }}>
                    {(() => {
                      const piloto = resultadosPilotos[1];
                      const socio = data.socios.find(s => s.id === piloto.socioId);
                      const foto = socio?.fotoUrl;
                      return (
                        <>
                          <img
                            src={foto || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(piloto.display_name || piloto.nombre || 'Piloto') + '&background=eee&color=666&size=128&rounded=true'}
                            alt="avatar"
                            style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: '3px solid silver',
                              boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                              background: '#f5f6fa'
                            }}
                          />
                          <div style={{
                            position: 'absolute',
                            bottom: '-10px',
                            right: '-10px',
                            backgroundColor: 'silver',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#333',
                            fontWeight: 'bold',
                            fontSize: '20px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                          }}>2</div>
                        </>
                      );
                    })()}
                  </div>
                  <div style={{ fontWeight: 'bold', fontSize: '16px', textAlign: 'center' }}>
                    {resultadosPilotos[1].display_name || resultadosPilotos[1].nombre || 'Piloto'}
                  </div>
                  <div style={{ color: '#0f3460', fontWeight: 'bold', fontSize: '18px' }}>
                    {resultadosPilotos[1].puntosTotales} pts
                  </div>
                </div>
                
                {/* Primer lugar */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  marginBottom: '0'
                }}>
                  <div style={{ 
                    width: '140px', 
                    height: '140px',
                    position: 'relative',
                    marginBottom: '10px'
                  }}>
                    {(() => {
                      const piloto = resultadosPilotos[0];
                      const socio = data.socios.find(s => s.id === piloto.socioId);
                      const foto = socio?.fotoUrl;
                      return (
                        <>
                          <img
                            src={foto || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(piloto.display_name || piloto.nombre || 'Piloto') + '&background=eee&color=666&size=128&rounded=true'}
                            alt="avatar"
                            style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: '4px solid gold',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
                              background: '#f5f6fa'
                            }}
                          />
                          <div style={{
                            position: 'absolute',
                            bottom: '-10px',
                            right: '-10px',
                            backgroundColor: 'gold',
                            borderRadius: '50%',
                            width: '50px',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#333',
                            fontWeight: 'bold',
                            fontSize: '24px',
                            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
                          }}>1</div>
                        </>
                      );
                    })()}
                  </div>
                  <div style={{ fontWeight: 'bold', fontSize: '20px', textAlign: 'center' }}>
                    {resultadosPilotos[0].display_name || resultadosPilotos[0].nombre || 'Piloto'}
                  </div>
                  <div style={{ color: '#0f3460', fontWeight: 'bold', fontSize: '22px' }}>
                    {resultadosPilotos[0].puntosTotales} pts
                  </div>
                </div>
                
                {/* Tercer lugar */}
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center',
                  marginBottom: '25px'
                }}>
                  <div style={{ 
                    width: '80px', 
                    height: '80px',
                    position: 'relative',
                    marginBottom: '10px'
                  }}>
                    {(() => {
                      const piloto = resultadosPilotos[2];
                      const socio = data.socios.find(s => s.id === piloto.socioId);
                      const foto = socio?.fotoUrl;
                      return (
                        <>
                          <img
                            src={foto || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(piloto.display_name || piloto.nombre || 'Piloto') + '&background=eee&color=666&size=128&rounded=true'}
                            alt="avatar"
                            style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: '50%',
                              objectFit: 'cover',
                              border: '3px solid #cd7f32',
                              boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                              background: '#f5f6fa'
                            }}
                          />
                          <div style={{
                            position: 'absolute',
                            bottom: '-10px',
                            right: '-10px',
                            backgroundColor: '#cd7f32',
                            borderRadius: '50%',
                            width: '35px',
                            height: '35px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '18px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                          }}>3</div>
                        </>
                      );
                    })()}
                  </div>
                  <div style={{ fontWeight: 'bold', fontSize: '14px', textAlign: 'center' }}>
                    {resultadosPilotos[2].display_name || resultadosPilotos[2].nombre || 'Piloto'}
                  </div>
                  <div style={{ color: '#0f3460', fontWeight: 'bold', fontSize: '16px' }}>
                    {resultadosPilotos[2].puntosTotales} pts
                  </div>
                </div>
              </div>
            </div>
          )}
          </DesktopOnly>

          {/* Podio y Clasificación General (Mobile) */}
          <MobileOnly>
            {resultadosPilotos.length >= 1 && (
              <>
                <CardTitle style={{ textAlign: 'center', color: theme.colors?.primary?.main || '#0f3460' }}>Podio</CardTitle>
                <MobileCardContainer style={{ marginBottom: '20px' }}>
                  {resultadosPilotos.slice(0, 3).map((piloto, index) => {
                    const socio = data.socios.find(s => s.id === piloto.socioId);
                    const foto = socio?.fotoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(piloto.display_name || piloto.nombre || 'P')}&background=eee&color=666&size=64&rounded=true`;
                    let podiumPosition = '';
                    if (index === 0) podiumPosition = '1º 🏆';
                    if (index === 1) podiumPosition = '2º 🥈';
                    if (index === 2) podiumPosition = '3º 🥉';

                    return (
                      <PilotoCardMobile key={piloto.socioId} theme={theme}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <img src={foto} alt={piloto.display_name || piloto.nombre} style={{ width: '50px', height: '50px', borderRadius: '50%', marginRight: '15px', objectFit: 'cover' }} />
                          <div>
                            <h4>{piloto.display_name || piloto.nombre || 'Piloto Desconocido'} {podiumPosition}</h4>
                            <p><span className="points">{piloto.puntosTotalesConDescarte ?? piloto.puntosTotales}</span> Puntos</p>
                          </div>
                        </div>
                      </PilotoCardMobile>
                    );
                  })}
                </MobileCardContainer>
              </>
            )}

            {resultadosPilotos.length > 0 && (
              <>
                <Card style={{ marginBottom: '20px' }}>
                  <div 
                    onClick={() => toggleSection('general')}
                    style={{ 
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      userSelect: 'none',
                      padding: '15px 20px',
                      margin: '-20px -20px 0 -20px',
                      borderBottom: expandedSections.general ? '1px solid #eee' : 'none',
                      transition: 'all 0.2s ease',
                      backgroundColor: expandedSections.general ? '#f8f9fa' : 'white',
                      borderRadius: '8px 8px 0 0'
                    }}
                  >
                    <h2 style={{ color: '#1a1a2e', margin: 0, fontSize: '1.2rem' }}>Clasificación General</h2>
                    <div style={{ 
                      transform: expandedSections.general ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                      fontSize: '0.9em',
                      opacity: 0.7,
                      padding: '5px 10px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '4px'
                    }}>
                      ▼
                    </div>
                  </div>
                  
                  <div style={{
                    maxHeight: expandedSections.general ? '5000px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.5s ease-in-out, opacity 0.3s ease',
                    opacity: expandedSections.general ? 1 : 0.8
                  }}>
                                         <div style={{ padding: expandedSections.general ? '15px 0 0 0' : '0' }}>
                       <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                         {resultadosPilotos.map((piloto, index) => {
                           const socio = data.socios.find(s => s.id === piloto.socioId);
                           const foto = socio?.fotoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(piloto.display_name || piloto.nombre || 'P')}&background=eee&color=666&size=64&rounded=true`;
                           const nombreCompleto = piloto.display_name || piloto.nombre || 'Piloto Desconocido';
                           
                           // Colores para las posiciones
                           let positionColor = '#6c757d';
                           let positionBg = '#f8f9fa';
                           if (index === 0) { positionColor = '#fff'; positionBg = '#FFD700'; } // Oro
                           else if (index === 1) { positionColor = '#fff'; positionBg = '#C0C0C0'; } // Plata
                           else if (index === 2) { positionColor = '#fff'; positionBg = '#CD7F32'; } // Bronce
                           else if (index < 5) { positionColor = '#fff'; positionBg = '#0f3460'; } // Top 5
                           
                           return (
                             <div
                               key={piloto.socioId}
                               style={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 background: 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)',
                                 borderRadius: '12px',
                                 padding: '16px',
                                 boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                 border: '1px solid #e9ecef',
                                 position: 'relative',
                                 overflow: 'hidden'
                               }}
                             >
                               {/* Posición */}
                               <div
                                 style={{
                                   width: '45px',
                                   height: '45px',
                                   borderRadius: '50%',
                                   backgroundColor: positionBg,
                                   color: positionColor,
                                   display: 'flex',
                                   alignItems: 'center',
                                   justifyContent: 'center',
                                   fontWeight: 'bold',
                                   fontSize: '18px',
                                   marginRight: '16px',
                                   flexShrink: 0,
                                   boxShadow: '0 2px 6px rgba(0,0,0,0.15)'
                                 }}
                               >
                                 {index + 1}
                               </div>
                               
                               {/* Foto del piloto */}
                               <div
                                 style={{
                                   width: '60px',
                                   height: '60px',
                                   borderRadius: '50%',
                                   overflow: 'hidden',
                                   marginRight: '16px',
                                   flexShrink: 0,
                                   border: '3px solid #fff',
                                   boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                                 }}
                               >
                                 <img 
                                   src={foto}
                                   alt={nombreCompleto}
                                   style={{ 
                                     width: '100%', 
                                     height: '100%', 
                                     objectFit: 'cover' 
                                   }} 
                                 />
                               </div>
                               
                               {/* Información del piloto */}
                               <div style={{ flex: 1, minWidth: 0 }}>
                                 <h3 style={{ 
                                   margin: '0 0 6px 0', 
                                   fontSize: '16px', 
                                   fontWeight: '600',
                                   color: '#1a1a2e',
                                   overflow: 'hidden',
                                   textOverflow: 'ellipsis',
                                   whiteSpace: 'nowrap'
                                 }}>
                                   {nombreCompleto}
                                 </h3>
                                 <div style={{ 
                                   fontSize: '14px', 
                                   color: '#6c757d',
                                   marginBottom: '4px'
                                 }}>
                                   <span style={{ 
                                     fontWeight: 'bold', 
                                     color: '#0f3460',
                                     fontSize: '16px'
                                   }}>
                                     {piloto.puntosTotalesConDescarte ?? piloto.puntosTotales}
                                   </span> pts
                                   {carreras.length > 1 && (
                                     <span style={{ marginLeft: '8px', opacity: 0.7 }}>
                                       ({piloto.puntosTotales} total)
                                     </span>
                                   )}
                                 </div>
                          {carreras.length > 1 && piloto.peorResultado && (
                                   <div style={{
                                     fontSize: '12px', 
                                     color: '#dc3545',
                                     background: '#fff5f5',
                                     padding: '4px 8px',
                                     borderRadius: '6px',
                                     border: '1px solid #fecaca',
                                     display: 'inline-block'
                                   }}>
                                     Descarte: {piloto.peorResultado.puntos} pts
                                   </div>
                          )}
                        </div>
                               
                               {/* Decoración para top 3 */}
                               {index < 3 && (
                                 <div style={{
                                   position: 'absolute',
                                   top: '8px',
                                   right: '12px',
                                   fontSize: '20px'
                                 }}>
                                   {index === 0 ? '🏆' : index === 1 ? '🥈' : '🥉'}
                      </div>
                               )}
                             </div>
                           );
                         })}
                       </div>
                     </div>
                  </div>
                </Card>
              </>
            )}
          </MobileOnly>
          
          {carreras.length > 1 && (
            <InfoMessage>
              La columna "Total con descarte" muestra la puntuación total descartando el peor resultado (marcado en rojo).
            </InfoMessage>
          )}
          
          <DesktopOnly>
            <Card style={{ marginBottom: '20px' }}>
            <div 
              onClick={() => toggleSection('general')}
              style={{ 
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                userSelect: 'none',
                padding: '15px 20px',
                margin: '-20px -20px 0 -20px',
                borderBottom: expandedSections.general ? '1px solid #eee' : 'none',
                transition: 'all 0.2s ease',
                backgroundColor: expandedSections.general ? '#f8f9fa' : 'white',
                borderRadius: '8px 8px 0 0'
              }}
            >
              <h2 style={{ color: '#1a1a2e', margin: 0, fontSize: '1.4rem' }}>Clasificación General</h2>
              <div style={{ 
                transform: expandedSections.general ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
                fontSize: '0.9em',
                opacity: 0.7,
                padding: '5px 10px',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px'
              }}>
                ▼
              </div>
            </div>
            
            <div style={{
              maxHeight: expandedSections.general ? '5000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.5s ease-in-out, opacity 0.3s ease',
              opacity: expandedSections.general ? 1 : 0.8
            }}>
              <div style={{ padding: expandedSections.general ? '20px 0 0 0' : '0' }}>
            <Table>
              <thead>
                <tr>
                  <Th style={{ width: '60px' }}>Pos.</Th>
                  <Th>Piloto</Th>
                  {carreras.map(carrera => (
                    <ThCarrera key={carrera.id} title={`${carrera.nombre} - ${carrera.circuito || 'Sin circuito'}`}>
                      {renderizarNombreCarrera(carrera)}
                    </ThCarrera>
                  ))}
                  <ThPuntos>Total</ThPuntos>
                  {carreras.length > 1 && (
                    <ThPuntos>Total con descarte</ThPuntos>
                  )}
                </tr>
              </thead>
              <tbody>
                {resultadosPilotos.map((piloto, index) => (
                  <tr key={piloto.socioId}>
                    <Td>
                      <PosicionContainer>
                        <Posicion pos={index + 1}>
                          {index < 3 ? <FaMedal /> : index + 1}
                        </Posicion>
                      </PosicionContainer>
                    </Td>
                    <TdPiloto style={{display:'flex',alignItems:'center',gap:10}}>
  {(() => {
    const socio = data.socios.find(s => s.id === piloto.socioId);
    const foto = socio?.fotoUrl;
    return (
      <img
        src={foto || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(piloto.display_name || piloto.nombre || 'Piloto') + '&background=eee&color=666&size=64&rounded=true'}
        alt="avatar"
        style={{width:42,height:42,borderRadius:'50%',objectFit:'cover',border:'2px solid #fff',boxShadow:'0 2px 8px #0002',background:'#f5f6fa'}}
      />
    );
  })()}
  {piloto.display_name || (piloto.nombre && piloto.apellido ? `${piloto.nombre} ${piloto.apellido}` : piloto.nombre || 'Sin nombre')}
</TdPiloto>
                    {carreras.map(carrera => (
                      <TdCarrera key={carrera.id}>
                        {renderizarPuntos(piloto, carrera.id)}
                      </TdCarrera>
                    ))}
                    <TdTotal>{piloto.puntosTotales}</TdTotal>
                    {carreras.length > 1 && (
                      <TdTotalDescarte>{piloto.puntosTotalesConDescarte}</TdTotalDescarte>
                    )}
                  </tr>
                ))}
              </tbody>
                </Table>
              </div>
            </div>
          </Card>
          </DesktopOnly>
          
          {/* Resultados individuales de cada carrera */}
          {/*<h2 style={{ marginTop: '20px', color: '#1a1a2e' }}>Resultados por Carrera</h2>*/}
          
          {carreras.map(carrera => {
            // Ordenar los resultados de la carrera por posición
            const resultadosCarrera = [...carrera.resultados].sort((a, b) => a.posicion - b.posicion);
            const isExpanded = expandedSections.carreras[carrera.id] || false;
            
            return (
              <React.Fragment key={carrera.id}>
                <DesktopOnly>
                  <Card style={{ marginBottom: '15px', overflow: 'hidden' }}>
                <CardTitle 
                  onClick={() => toggleSection(carrera.id)}
                  style={{ 
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    userSelect: 'none',
                    paddingRight: '20px',
                    marginBottom: isExpanded ? '15px' : '0',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div>
                    {carrera.nombre} - {formatearFecha(carrera.fecha)}
                    {carrera.circuito && <span style={{ fontWeight: 'normal', marginLeft: '10px' }}>({carrera.circuito})</span>}
                  </div>
                  <div style={{ 
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                    fontSize: '0.8em',
                    opacity: 0.7
                  }}>
                    ▼
                  </div>
                </CardTitle>
                
                <div style={{
                  maxHeight: isExpanded ? '5000px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.5s ease-in-out, opacity 0.3s ease',
                  opacity: isExpanded ? 1 : 0.8
                }}>
                
                <Table>
                  <thead>
                    <tr>
                      <Th style={{ width: '60px' }}>Pos.</Th>
                      <Th>Piloto</Th>
                      <Th style={{ textAlign: 'center' }}>Puntos</Th>
                      <Th style={{ textAlign: 'center' }}>Extras</Th>
                      <Th style={{ textAlign: 'center' }}>Sanciones</Th>
                      <Th style={{ textAlign: 'center' }}>DNF</Th>
                      <Th style={{ textAlign: 'center' }}>DNS</Th>
                      <Th style={{ textAlign: 'center' }}>Total</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultadosCarrera.map(resultado => {
                      // Buscar el piloto correspondiente
                      const piloto = data.socios.find(s => s.id === resultado.socioId);
                      const nombrePiloto = piloto ? (piloto.display_name || (piloto.nombre && piloto.apellido ? `${piloto.nombre} ${piloto.apellido}` : piloto.nombre || 'Sin nombre')) : 'Piloto no encontrado';
                      
                      // Calcular extras
                      const extras = [];
                      if (resultado.vueltaRapida) extras.push('VR (+1)');
                      if (resultado.pole || resultado.polePosition) extras.push('Pole (+0)');
                      if (resultado.mejorConsistencia) extras.push('MC (+2)');
                      if (resultado.segundaMejorConsistencia) extras.push('SMC (+1)');
                      
                      // Buscar sanciones aplicadas a este piloto en esta carrera
                      const sancionesPiloto = data.sanciones.filter(sancion => 
                        sancion.socioId === resultado.socioId && sancion.carreraId === carrera.id
                      );
                      
                      // Preparar la visualización de las sanciones con enlaces
                      const sancionesElementos = sancionesPiloto.map((sancion, index) => (
                        <div 
                          key={sancion.id || index}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSancionSeleccionada(sancion);
                          }}
                          style={{
                            cursor: 'pointer',
                            color: '#0f6bff',
                            textDecoration: 'underline',
                            display: 'inline-flex',
                            alignItems: 'center',
                            marginRight: '10px',
                            marginBottom: '5px',
                          }}
                        >
                          {sancion.nombre} ({sancion.puntosPenalizacion < 0 ? '' : '+'}{sancion.puntosPenalizacion})
                          <FaInfoCircle style={{ marginLeft: '4px', fontSize: '0.9em' }} />
                        </div>
                      ));
                      
                      // También incluir las sanciones registradas en el resultado
                      if (resultado.sancionesAplicadas && resultado.sancionesAplicadas.length > 0) {
                        resultado.sancionesAplicadas.forEach(sancionId => {
                          // Verificar si la sanción ya está incluida
                          const yaIncluida = sancionesPiloto.some(s => s.id === sancionId);
                          if (!yaIncluida) {
                            const sancion = data.sanciones.find(s => s.id === sancionId);
                            if (sancion) {
                              sancionesElementos.push(
                                <div 
                                  key={sancion.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSancionSeleccionada(sancion);
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    color: '#0f6bff',
                                    textDecoration: 'underline',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    marginRight: '10px',
                                    marginBottom: '5px',
                                  }}
                                >
                                  {sancion.nombre} ({sancion.puntosPenalizacion < 0 ? '' : '+'}{sancion.puntosPenalizacion})
                                  <FaInfoCircle style={{ marginLeft: '4px', fontSize: '0.9em' }} />
                                </div>
                              );
                            }
                          }
                        });
                      }
                      
                      return (
                        <tr key={resultado.socioId}>
                          <Td>
                            <PosicionContainer>
                              <Posicion pos={resultado.posicion}>
                                {resultado.posicion <= 3 ? <FaMedal /> : resultado.posicion}
                              </Posicion>
                            </PosicionContainer>
                          </Td>
                          <TdPiloto style={{display:'flex',alignItems:'center',gap:10}}>
                            {(() => {
                              const foto = piloto?.fotoUrl;
                              return (
                                <img
                                  src={foto || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(nombrePiloto) + '&background=eee&color=666&size=64&rounded=true'}
                                  alt="avatar"
                                  style={{width:32,height:32,borderRadius:'50%',objectFit:'cover',border:'2px solid #fff',boxShadow:'0 2px 8px #0002',background:'#f5f6fa'}}
                                />
                              );
                            })()}
                            {nombrePiloto}
                          </TdPiloto>
                          <TdCarrera style={{ textAlign: 'center' }}>{resultado.puntos}</TdCarrera>
                          <TdCarrera style={{ textAlign: 'center' }}>{extras.length > 0 ? extras.join(', ') : '-'}</TdCarrera>
                          <TdCarrera style={{ textAlign: 'center', color: sancionesElementos.length > 0 ? '#e94560' : 'inherit' }}>
                            {sancionesElementos.length > 0 ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '5px' }}>
                                {sancionesElementos}
                              </div>
                            ) : '-'}
                          </TdCarrera>
                          <TdCarrera style={{ textAlign: 'center' }}>
                            {resultado.dnf ? (
                              <FaTimes style={{ color: '#ff6b6b', fontSize: '1.2em' }} title="No terminó la carrera" />
                            ) : (
                              <FaCheck style={{ color: '#4CAF50', fontSize: '1.2em' }} title="Terminó la carrera" />
                            )}
                          </TdCarrera>
                          <TdCarrera style={{ textAlign: 'center' }}>
                            {carrera.pilotosNoPresentados?.includes(resultado.socioId) ? (
                              <FaTimes style={{ color: '#ff6b6b', fontSize: '1.2em' }} title="No se presentó a la carrera" />
                            ) : (
                              <FaCheck style={{ color: '#4CAF50', fontSize: '1.2em' }} title="Se presentó a la carrera" />
                            )}
                          </TdCarrera>
                          <TdTotal>
                            {/* Recalcular los puntos totales para asegurarnos de que las sanciones están aplicadas correctamente */}
                            {(() => {
                              // Puntos base por posición
                              let puntos = resultado.puntos;
                              
                              // Puntos adicionales por extras
                              let puntosExtras = resultado.puntosAdicionales || 0;
                              
                              // Puntos de sanciones
                              let puntosSanciones = 0;
                              
                              // Sumar los puntos de las sanciones encontradas
                              sancionesPiloto.forEach(sancion => {
                                puntosSanciones += sancion.puntosPenalizacion;
                              });
                              
                              // Calcular total
                              // Si es DNF, los puntos de posición son 0, pero puede recibir puntos por sanciones positivas
                              // Si no se ha presentado, siempre 0 puntos
                              let total = 0;
                              if (resultado.noParticipacion) {
                                // No se presentó, 0 puntos base pero puede recibir puntos negativos por sanciones
                                total = puntosSanciones < 0 ? puntosSanciones : 0;
                              } else if (resultado.dnf) {
                                // DNF: 0 puntos por posición, pero puede recibir puntos por sanciones positivas
                                total = puntosSanciones > 0 ? puntosSanciones : 0;
                              } else {
                                // Normal: puntos por posición + adicionales + sanciones
                                total = puntos + puntosExtras + puntosSanciones;
                              }
                              
                              return total;
                            })()}
                          </TdTotal>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </Card>
          </DesktopOnly>

          <MobileOnly>
            <Card style={{ marginBottom: '15px' }}>
              <CardTitle 
                onClick={() => toggleSection(carrera.id)}
                style={{
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  userSelect: 'none',
                  paddingRight: '20px',
                  marginBottom: isExpanded ? '15px' : '0',
                  transition: 'all 0.2s ease'
                }}
              >
                <div>
                  {carrera.nombre} - {formatearFecha(carrera.fecha)}
                  {carrera.circuito && <span style={{ fontWeight: 'normal', marginLeft: '10px' }}>({carrera.circuito})</span>}
                </div>
                <div style={{ 
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  fontSize: '0.8em',
                  opacity: 0.7
                }}>
                  ▼
                </div>
              </CardTitle>
              <div style={{
                maxHeight: isExpanded ? '5000px' : '0',
                overflow: 'hidden',
                transition: 'max-height 0.5s ease-in-out, opacity 0.3s ease',
                opacity: isExpanded ? 1 : 0.8
              }}>
                {resultadosCarrera.length > 0 ? (
                  <MobileCardContainer>
                    {resultadosCarrera.map(resultado => {
                      const piloto = data.socios.find(s => s.id === resultado.socioId);
                      const nombrePiloto = piloto ? (piloto.display_name || (piloto.nombre && piloto.apellido ? `${piloto.nombre} ${piloto.apellido}` : piloto.nombre || 'Sin nombre')) : 'Piloto no encontrado';
                      const foto = piloto?.fotoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(nombrePiloto)}&background=eee&color=666&size=64&rounded=true`;
                      
                      // Recalcular puntos totales para móvil
                      let puntosBase = resultado.puntos;
                      let puntosExtras = resultado.puntosAdicionales || 0;
                      let puntosSanciones = 0;
                      const sancionesPiloto = data.sanciones.filter(sancion => sancion.socioId === resultado.socioId && sancion.carreraId === carrera.id);
                      sancionesPiloto.forEach(sancion => { puntosSanciones += sancion.puntosPenalizacion; });
                      if (resultado.sancionesAplicadas && resultado.sancionesAplicadas.length > 0) {
                        resultado.sancionesAplicadas.forEach(sancionId => {
                          const yaIncluida = sancionesPiloto.some(s => s.id === sancionId);
                          if (!yaIncluida) {
                            const sancion = data.sanciones.find(s => s.id === sancionId);
                            if (sancion) puntosSanciones += sancion.puntosPenalizacion;
                          }
                        });
                      }
                      let totalPuntosCarrera = 0;
                      if (resultado.noParticipacion) {
                        totalPuntosCarrera = puntosSanciones < 0 ? puntosSanciones : 0;
                      } else if (resultado.dnf) {
                        totalPuntosCarrera = puntosSanciones > 0 ? puntosSanciones : 0;
                      } else {
                        totalPuntosCarrera = puntosBase + puntosExtras + puntosSanciones;
                      }

                      return (
                        <PilotoCardMobile key={resultado.socioId} theme={theme}>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                             <Posicion pos={resultado.posicion} style={{marginRight: '10px', width: '35px', height: '35px', flexShrink: 0}}>
                                {resultado.posicion <= 3 && resultado.posicion > 0 ? <FaMedal /> : resultado.posicion > 0 ? resultado.posicion : '-'}
                             </Posicion>
                            <img src={foto} alt={nombrePiloto} style={{ width: '40px', height: '40px', borderRadius: '50%', marginRight: '10px', objectFit: 'cover' }} />
                            <div>
                              <h4>{nombrePiloto}</h4>
                              <p><span className="points">{totalPuntosCarrera}</span> Puntos</p>
                              {resultado.dnf && <p style={{color: theme.colors?.error?.main || 'red', fontSize: '0.8em'}}>DNF</p>}
                              {resultado.noParticipacion && <p style={{color: theme.colors?.text?.disabled || 'grey', fontSize: '0.8em'}}>DNS</p>}
                              {sancionesPiloto.length > 0 && (
                                <p style={{fontSize: '0.8em', color: theme.colors?.warning?.main || 'orange'}}>
                                  Sanciones: {sancionesPiloto.map(s => `${s.nombre} (${s.puntosPenalizacion})`).join(', ')}
                                </p>
                              )}
                            </div>
                          </div>
                        </PilotoCardMobile>
                      );
                    })}
                  </MobileCardContainer>
                ) : (
                  <EmptyMessage>No hay resultados para esta carrera.</EmptyMessage>
                )}
              </div>
            </Card>
          </MobileOnly>
        </React.Fragment>
      );
        })}
      </div>
      )}
      
      <SancionModal 
        sancion={sancionSeleccionada} 
        onClose={() => setSancionSeleccionada(null)} 
      />
    </Container>
  );
};

export default ClasificacionDetail;
